import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateCaseModal from '../cases-list/components/CreateCaseModal';
import ReportCaseIncidentModal from './components/ReportCaseIncidentModal';
import DentistIncidentTrackingModal from './components/DentistIncidentTrackingModal';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { loadDentistHubData, formatMoMTrend } from './dentistHubData';

const nowLabel = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}).format(new Date());

const emptyStats = {
  activeCount: 0,
  urgentCount: 0,
  unreadCount: 0,
  draftCount: 0,
  newPatientsThisMonth: 0,
  newPatientsPrevMonth: 0,
  completedThisMonth: 0,
  completedPrevMonth: 0,
};

function greetingWithName(fullName) {
  const n = (fullName || '').trim();
  if (!n) return 'Bonjour';
  if (/^(dr\.?|docteur)\s/i.test(n)) return `Bonjour, ${n}`;
  return `Bonjour, Dr. ${n}`;
}

function formatRelativeFr(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 45) return 'À l’instant';
  const min = Math.floor(sec / 60);
  if (min < 60) return `Il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `Il y a ${d} j`;
  return new Date(iso).toLocaleDateString('fr-FR');
}

const trendColorClass = (s) => {
  if (!s) return 'text-slate-500';
  if (s.startsWith('+')) return 'text-emerald-700';
  if (s.startsWith('-')) return 'text-red-700';
  return 'text-slate-500';
};

function KpiGridSkeleton() {
  return (
    <section
      className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3"
      aria-busy="true"
      aria-label="Chargement des indicateurs"
    >
      {Array.from({ length: 4 }, (_, i) => (
        <div
          key={i}
          className="rounded-xl border border-slate-200 bg-white p-3 md:p-3.5 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03] animate-pulse"
        >
          <div className="flex justify-between gap-3">
            <div className="h-2.5 w-24 rounded bg-slate-200" />
            <div className="h-9 w-9 shrink-0 rounded-lg bg-slate-200" />
          </div>
          <div className="mt-2 h-7 w-16 rounded-md bg-slate-200" />
          <div className="mt-2 h-2.5 w-28 rounded bg-slate-100" />
        </div>
      ))}
    </section>
  );
}

function HubColumnListSkeleton({ rows = 3 }) {
  return (
    <ul className="space-y-1.5 flex-1" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <li
          key={i}
          className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-2 animate-pulse"
        >
          <div className="h-8 w-8 shrink-0 rounded-md bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-1 py-0.5">
            <div className="h-3 w-[70%] max-w-[200px] rounded bg-slate-200" />
            <div className="h-2.5 w-16 rounded bg-slate-100" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function DraftListSkeleton() {
  return (
    <ul className="space-y-1.5 flex-1 min-h-0 overflow-hidden" aria-busy="true">
      {Array.from({ length: 3 }, (_, i) => (
        <li
          key={i}
          className="flex w-full gap-2 rounded-lg border border-slate-200 bg-white p-2 animate-pulse"
        >
          <div className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex gap-2">
              <div className="h-4 w-14 rounded-full bg-slate-200" />
              <div className="h-3 flex-1 max-w-[160px] rounded bg-slate-200" />
            </div>
            <div className="h-2.5 w-40 rounded bg-slate-100" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function IncidentsSkeleton() {
  return (
    <ul className="grid gap-1.5 sm:grid-cols-2" aria-busy="true">
      {Array.from({ length: 2 }, (_, i) => (
        <li key={i} className="rounded-lg border border-slate-200 bg-slate-50/80 p-2 animate-pulse">
          <div className="h-3 w-[85%] rounded bg-slate-200" />
          <div className="mt-1.5 h-2.5 w-36 rounded bg-slate-100" />
        </li>
      ))}
    </ul>
  );
}

const DentistHubPage = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { setTheme } = useTheme();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hubRefreshing, setHubRefreshing] = useState(false);
  const [createCaseOpen, setCreateCaseOpen] = useState(false);
  const [reportIncidentOpen, setReportIncidentOpen] = useState(false);
  const [trackingIncidentId, setTrackingIncidentId] = useState(null);
  const [userRole, setUserRole] = useState('dentist_client');
  const [displayName, setDisplayName] = useState('');
  const [hub, setHub] = useState({
    stats: { ...emptyStats },
    queueCounts: { nonSoumis: 0, aValider: 0 },
    messageSamples: [],
    recentIncidents: [],
    draftCases: [],
  });

  const refreshHub = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const data = await loadDentistHubData(uid);
      setHub(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setCurrentUserId(user.id);

      const [{ data: profile }, { data: roleAssignments }] = await Promise.all([
        supabase.from('user_profiles').select('full_name').eq('id', user.id).maybeSingle(),
        supabase
          .from('user_role_assignments')
          .select('user_roles(role_type)')
          .eq('user_id', user.id)
          .eq('is_active', true),
      ]);

      setDisplayName(profile?.full_name || '');

      const types = (roleAssignments || [])
        .map((r) => r.user_roles?.role_type)
        .filter(Boolean);
      if (types.includes('administrator')) {
        setUserRole('administrator');
      } else {
        setUserRole(types[0] || 'dentist_client');
      }

      if (!types.includes('administrator') && types.includes('dentist_client') && !localStorage.getItem('appTheme')) {
        setTheme('neutral');
      }

      await refreshHub(user.id);
      setLoading(false);
    })();
  }, [navigate, refreshHub, setTheme]);

  const messagesListUnreadLink = '/cases-list?messages=unread';

  const trendPatients = formatMoMTrend(hub.stats.newPatientsThisMonth, hub.stats.newPatientsPrevMonth);
  const trendCompleted = formatMoMTrend(hub.stats.completedThisMonth, hub.stats.completedPrevMonth);

  const kpiCards = [
    {
      label: 'Commandes actives',
      value: hub.stats.activeCount,
      trend: null,
      icon: 'Briefcase',
      iconGradient: 'from-blue-600 to-indigo-700',
      to: '/cases-list',
    },
    {
      label: 'Nouveaux patients (mois)',
      value: hub.stats.newPatientsThisMonth,
      trend: trendPatients,
      icon: 'UserPlus',
      iconGradient: 'from-violet-600 to-purple-700',
      to: '/cases-list',
    },
    {
      label: 'Commandes terminées (mois)',
      value: hub.stats.completedThisMonth,
      trend: trendCompleted,
      icon: 'CheckCircle',
      iconGradient: 'from-amber-500 to-orange-600',
      to: '/cases-list',
    },
    {
      label: 'Messages non lus',
      value: hub.stats.unreadCount,
      trend: null,
      icon: 'MessageCircle',
      iconGradient: 'from-sky-500 to-cyan-600',
      to: messagesListUnreadLink,
    },
  ];

  const handleManualRefreshHub = useCallback(async () => {
    if (!currentUserId) return;
    setHubRefreshing(true);
    try {
      await refreshHub(currentUserId);
    } finally {
      setHubRefreshing(false);
    }
  }, [currentUserId, refreshHub]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb
          items={[{ label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' }]}
        />
        <div className="p-3 md:p-4 max-w-7xl mx-auto space-y-4 md:space-y-5">
          <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 md:gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
                {greetingWithName(displayName)}
              </h1>
              <p className="text-xs md:text-sm text-slate-600 mt-1 max-w-xl font-medium">
                Bienvenue dans votre espace de pilotage de vos commandes
              </p>
            </div>
            <div className="text-right shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date du jour</p>
              <p className="text-xs font-bold text-slate-900 mt-0.5 capitalize">{nowLabel}</p>
            </div>
          </header>

          {loading ? (
            <KpiGridSkeleton />
          ) : (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3" aria-label="Indicateurs">
            {kpiCards.map((k) => (
              <Link
                key={k.label}
                to={k.to}
                className="relative block cursor-pointer rounded-xl border border-slate-200 bg-white p-3 md:p-3.5 text-left shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03] hover:border-blue-300/80 hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/40 overflow-hidden no-underline text-inherit"
              >
                <div
                  className={`pointer-events-none absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${k.iconGradient} text-white shadow-md`}
                  aria-hidden
                >
                  <Icon name={k.icon} size={18} className="text-white" />
                </div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide pr-12">{k.label}</p>
                <p className="text-xl md:text-2xl font-extrabold text-slate-900 tabular-nums mt-1">{k.value}</p>
                {k.trend ? (
                  <p className={`text-[11px] font-bold mt-1 tabular-nums flex flex-wrap items-center gap-x-1 gap-y-0 ${trendColorClass(k.trend)}`}>
                    <span className="text-sm leading-none" aria-hidden>
                      {k.trend.startsWith('+') ? '↗' : k.trend.startsWith('-') ? '↘' : ''}
                    </span>
                    <span>{k.trend}</span>
                    <span className="text-slate-500 font-semibold text-[9px] uppercase tracking-wide">
                      vs mois dernier
                    </span>
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1">&nbsp;</p>
                )}
              </Link>
            ))}
          </section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 lg:items-stretch">
            {/* Colonne 1 — Actions rapides */}
            <section className="lg:col-span-4 lg:flex lg:flex-col lg:h-full lg:min-h-0 space-y-2" aria-label="Actions rapides">
              <div className="rounded-xl border border-slate-200 bg-white p-3 md:p-3.5 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03] flex flex-col flex-1 min-h-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md shadow-blue-600/25">
                    <Icon name="Calendar" size={17} />
                  </span>
                  <h2 className="text-xs font-extrabold text-slate-900 uppercase tracking-wide">Actions rapides</h2>
                </div>
                <ul className="space-y-1.5 flex-1 min-h-0">
                  {[
                    {
                      title: 'Nouvelle commande',
                      icon: 'Plus',
                      iconClass: 'bg-gradient-to-br from-violet-500 to-purple-700 text-white shadow-md shadow-violet-600/25',
                      onClick: () => setCreateCaseOpen(true),
                    },
                    {
                      title: 'Lancer la suite',
                      icon: 'Package',
                      iconClass:
                        'bg-gradient-to-br from-teal-500 to-emerald-700 text-white shadow-md shadow-teal-600/25',
                      to: '/cases-list?conception=expediee',
                    },
                    {
                      title: 'Incident sur conception',
                      icon: 'AlertTriangle',
                      iconClass: 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md shadow-orange-500/30',
                      onClick: () => setReportIncidentOpen(true),
                    },
                  ].map((item) => {
                    const rowClass =
                      'flex w-full cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-2 text-left transition-all hover:bg-white hover:border-blue-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 no-underline text-inherit';
                    return (
                      <li key={item.title}>
                        {'to' in item && item.to ? (
                          <Link to={item.to} className={rowClass}>
                            <span
                              className={`pointer-events-none flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${item.iconClass}`}
                              aria-hidden
                            >
                              <Icon name={item.icon} size={17} />
                            </span>
                            <span className="min-w-0 flex-1 font-semibold text-xs text-slate-900">{item.title}</span>
                            <ChevronRight className="w-4 h-4 shrink-0 text-blue-600" aria-hidden />
                          </Link>
                        ) : (
                          <button type="button" onClick={item.onClick} className={rowClass}>
                            <span
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${item.iconClass}`}
                            >
                              <Icon name={item.icon} size={17} />
                            </span>
                            <span className="min-w-0 flex-1 font-semibold text-xs text-slate-900">{item.title}</span>
                            <ChevronRight className="w-4 h-4 shrink-0 text-blue-600" aria-hidden />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/cases-list"
                  className="mt-2 inline-flex cursor-pointer items-center justify-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Voir toutes mes conceptions
                  <ChevronRight className="w-4 h-4" aria-hidden />
                </Link>
              </div>
            </section>

            {/* Colonne 2 — Actions requises */}
            <section className="lg:col-span-4 lg:flex lg:flex-col lg:h-full lg:min-h-0 space-y-2" aria-label="Actions requises">
              <div className="rounded-xl border border-slate-200 bg-white p-3 md:p-3.5 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03] flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 text-white shadow-md shadow-cyan-500/25">
                      <Icon name="Activity" size={17} />
                    </span>
                    <h2 className="text-xs font-extrabold text-slate-900 uppercase tracking-wide truncate">
                      Actions requises
                    </h2>
                  </div>
                  {currentUserId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={hubRefreshing}
                      disabled={loading}
                      onClick={handleManualRefreshHub}
                    >
                      Actualiser
                    </Button>
                  )}
                </div>
                {loading ? (
                  <HubColumnListSkeleton rows={3} />
                ) : (
                  <ul className="space-y-1.5 flex-1 min-h-0">
                    {[
                      {
                        key: 'unread',
                        title:
                          hub.stats.unreadCount > 0
                            ? `Messages non lus (${hub.stats.unreadCount})`
                            : 'Messages non lus',
                        to: messagesListUnreadLink,
                        icon: 'MessageCircle',
                        iconClass:
                          'bg-gradient-to-br from-sky-500 to-cyan-600 text-white shadow-md shadow-cyan-500/25',
                      },
                      {
                        key: 'aValider',
                        title: `Maquette en attente de confirmation (${hub.queueCounts?.aValider ?? 0})`,
                        subtitle:
                          "Facturation automatique des conceptions après 30 jours d'inactivité",
                        to: '/cases-list?conception=aValider',
                        icon: 'ClipboardCheck',
                        iconClass:
                          'bg-gradient-to-br from-violet-500 to-purple-700 text-white shadow-md shadow-violet-600/25',
                      },
                      {
                        key: 'nonSoumise',
                        title: `Soumission en attente (${hub.queueCounts?.nonSoumis ?? 0})`,
                        to: '/cases-list?conception=nonSoumise',
                        icon: 'Send',
                        iconClass:
                          'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md shadow-orange-500/30',
                      },
                    ].map((item) => {
                      const rowClass =
                        'flex w-full cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-2 text-left transition-all hover:bg-white hover:border-blue-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 no-underline text-inherit';
                      return (
                        <li key={item.key}>
                          <Link to={item.to} className={rowClass}>
                            <span
                              className={`pointer-events-none flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${item.iconClass}`}
                              aria-hidden
                            >
                              <Icon name={item.icon} size={17} />
                            </span>
                            <span className="min-w-0 flex-1 flex flex-col gap-0">
                              <span className="font-semibold text-xs text-slate-900 leading-snug">{item.title}</span>
                              {item.subtitle ? (
                                <span className="text-[10px] text-slate-600 font-medium leading-snug line-clamp-2">
                                  {item.subtitle}
                                </span>
                              ) : null}
                            </span>
                            <ChevronRight className="w-4 h-4 shrink-0 text-blue-600" aria-hidden />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

            {/* Colonne 3 — Commandes en cours (brouillons non soumis, max 5 affichées) */}
            <section className="lg:col-span-4 lg:flex lg:flex-col lg:h-full lg:min-h-0 space-y-2" aria-label="Commandes en cours">
              <div className="rounded-xl border border-slate-200 bg-white p-3 md:p-3.5 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03] flex flex-col flex-1 min-h-0">
                <div className="flex items-center gap-2 mb-2 shrink-0">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-700 text-white shadow-md shadow-emerald-600/25">
                    <Icon name="FileEdit" size={17} />
                  </span>
                  <h2 className="text-xs font-extrabold text-slate-900 uppercase tracking-wide">Commandes en cours</h2>
                </div>
                {loading ? (
                  <DraftListSkeleton />
                ) : !hub.draftCases?.length ? (
                  <div className="flex-1 flex flex-col justify-center min-h-0 py-1 space-y-2">
                    <p className="text-xs text-slate-600 font-medium leading-snug">
                      Aucun brouillon en cours — vos conceptions non encore soumises apparaîtront ici.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1.5">
                      <Button type="button" size="sm" className="h-8 text-xs" onClick={() => setCreateCaseOpen(true)}>
                        Nouvelle commande
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="h-8 text-xs" asChild>
                        <Link to="/cases-list?conception=nonSoumise">Voir les non soumises</Link>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ul className="space-y-1.5 flex-1 min-h-0 overflow-y-auto overscroll-contain pr-0.5 -mr-0.5">
                    {(hub.draftCases ?? []).slice(0, 5).map((d) => (
                      <li key={d.id}>
                        <Link
                          to={`/case/${d.id}/assistant`}
                          className="flex w-full cursor-pointer gap-2 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-sm transition-all hover:border-emerald-200/90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/30 no-underline text-inherit"
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 rounded-full border-2 border-amber-400 bg-amber-50" aria-hidden />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 gap-y-0">
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0 rounded-full bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200/90">
                                Brouillon
                              </span>
                              <p className="text-xs font-bold text-slate-900 truncate">
                                {d.patientName} — non soumis
                              </p>
                            </div>
                            <p className="text-[10px] text-slate-600 font-medium mt-0.5">
                              Conception #{d.caseNumber} · poursuivre la saisie
                            </p>
                          </div>
                          <ChevronRight className="w-3.5 h-3.5 shrink-0 text-emerald-600 mt-0.5" aria-hidden />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/cases-list?conception=nonSoumise"
                  className="mt-2 shrink-0 inline-flex cursor-pointer items-center justify-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Voir toutes les conceptions non soumises
                  <ChevronRight className="w-4 h-4" aria-hidden />
                </Link>
              </div>
            </section>
          </div>

          <section className="rounded-xl border-y border-r border-slate-200 border-l-[4px] border-l-red-600 bg-white p-3 md:p-4 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700">
                  <Icon name="AlertTriangle" size={17} />
                </span>
                Incidents récents
              </h2>
              <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/cases-list?hasIncidents=yes')}>
                Voir tout
              </Button>
            </div>
            {loading ? (
              <IncidentsSkeleton />
            ) : !hub.recentIncidents?.length ? (
              <div className="py-1 space-y-2">
                <p className="text-xs text-slate-600 font-medium">Aucun incident récent.</p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1.5">
                  <Button type="button" size="sm" className="h-8 text-xs" variant="secondary" onClick={() => setReportIncidentOpen(true)}>
                    Déclarer un incident
                  </Button>
                  <Button type="button" size="sm" className="h-8 text-xs" variant="outline" asChild>
                    <Link to="/cases-list?hasIncidents=yes">Conceptions avec incident</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {hub.recentIncidents.map((incident) => (
                  <li
                    key={incident.id}
                    className="rounded-lg border border-slate-200 bg-slate-50/80 p-2 hover:bg-white hover:border-red-200 transition-colors shadow-sm"
                  >
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-900 truncate">
                          {incident.title} — Conception #{incident.caseNumber}
                        </p>
                        <p className="text-[10px] text-slate-600 font-medium mt-0.5">
                          {incident.patientName} · {incident.reportedDate}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="shrink-0"
                        onClick={() => setTrackingIncidentId(incident.id)}
                      >
                        Ouvrir
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      {currentUserId && (
        <>
          <CreateCaseModal
            isOpen={createCaseOpen}
            onClose={() => setCreateCaseOpen(false)}
            currentUserId={currentUserId}
            currentUserRole={userRole}
            allowAddPatient
            onCaseCreated={(caseData) => {
              setCreateCaseOpen(false);
              refreshHub(currentUserId);
              navigate(`/case/${caseData.id}/assistant`);
            }}
          />
          <ReportCaseIncidentModal
            isOpen={reportIncidentOpen}
            onClose={() => setReportIncidentOpen(false)}
            currentUserId={currentUserId}
            onIncidentCreated={() => {
              setReportIncidentOpen(false);
              refreshHub(currentUserId);
            }}
          />
          <DentistIncidentTrackingModal
            isOpen={!!trackingIncidentId}
            onClose={() => setTrackingIncidentId(null)}
            incidentId={trackingIncidentId}
            dentistUserId={currentUserId}
          />
        </>
      )}
    </div>
  );
};

export default DentistHubPage;
